import React from "react";
import { api } from "../../lib/api";

export default function PollMessage({ message, onUpdate }) {
  const meId = localStorage.getItem("userID");
  const poll = message.poll;
  if (!poll) return null;

  const totalVotes = (poll.options || []).reduce((s, o) => s + (o.votes?.length || 0), 0);
  const expired = poll.expiresAt && new Date(poll.expiresAt) < new Date();
  const closed = poll.closed || expired;
  const voted = (poll.options || []).some((o) => (o.votes || []).some((u) => String(u._id || u) === String(meId)));
  const isAuthor = String(message.author?._id || message.author) === String(meId);

  async function vote(optionIndex) {
    if (closed) return;
    try {
      const r = await api(`/chat/messages/${message._id}/poll/vote`, {
        method: "POST",
        body: JSON.stringify({ optionIndex }),
      });
      onUpdate && onUpdate(r.poll);
    } catch (e) { /* surfaced via toast in parent if needed */ }
  }

  async function close() {
    try {
      await api(`/chat/messages/${message._id}/poll/close`, { method: "POST" });
    } catch (e) {}
  }

  return (
    <div className="bg-surface border border-blue-200 rounded-md p-3 max-w-md">
      <div className="flex items-start gap-2 mb-2">
        <span className="text-lg">📊</span>
        <div className="flex-1">
          <div className="text-sm font-semibold text-ink-800">{poll.question}</div>
          <div className="text-[10px] text-ink-500 mt-0.5">
            {poll.multi ? "Multiple choice" : "Single choice"}
            {poll.anonymous && " · Anonymous"}
            {poll.expiresAt && (
              <span className={expired ? " · text-red-500 font-semibold" : ""}>
                {" · "}{expired ? "Expired" : `Until ${new Date(poll.expiresAt).toLocaleString()}`}
              </span>
            )}
            {closed && !expired && " · Closed"}
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        {(poll.options || []).map((opt, i) => {
          const count = (opt.votes || []).length;
          const pct = totalVotes ? Math.round((count / totalVotes) * 100) : 0;
          const meVoted = (opt.votes || []).some((u) => String(u._id || u) === String(meId));
          return (
            <button
              key={opt._id || i}
              onClick={() => vote(i)}
              disabled={closed}
              className={[
                "w-full relative px-3 py-2 rounded text-left text-sm transition border",
                meVoted ? "border-blue-400 bg-blue-50" : "border-ink-200 hover:bg-ink-50",
                closed ? "cursor-not-allowed opacity-80" : "cursor-pointer",
              ].join(" ")}
            >
              <div
                className="absolute inset-y-0 left-0 bg-blue-100 rounded transition-all"
                style={{ width: `${pct}%`, opacity: 0.5 }}
              />
              <div className="relative flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  {meVoted && <span className="text-blue-600">✓</span>}
                  <span>{opt.text}</span>
                </span>
                <span className="text-xs font-mono text-ink-600">{count} ({pct}%)</span>
              </div>
              {!poll.anonymous && (opt.votes || []).length > 0 && (
                <div className="relative mt-1 flex -space-x-1.5">
                  {(opt.votes || []).slice(0, 5).map((u) => (
                    <span key={u._id || u} className="w-4 h-4 rounded-full bg-ink-300 text-white text-[8px] font-semibold flex items-center justify-center border-[1.5px] border-white"
                      title={u.username || ""}>
                      {(u.username || "?").charAt(0).toUpperCase()}
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex items-center justify-between text-[10px] text-ink-400">
        <span>{totalVotes} vote{totalVotes !== 1 ? "s" : ""}</span>
        {isAuthor && !closed && (
          <button onClick={close} className="text-ink-500 hover:text-red-500">Close poll</button>
        )}
      </div>
    </div>
  );
}
